import { useState } from "react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface FieldPilotAddCustomLineItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (args: { rawValue: string }) => Promise<void>;
}

export function FieldPilotAddCustomLineItemDialog({
  open,
  onOpenChange,
  onCreate,
}: FieldPilotAddCustomLineItemDialogProps) {
  const [name, setName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async () => {
    if (isSaving) return;
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error("Custom line item name is required");
      return;
    }

    setIsSaving(true);
    try {
      await onCreate({ rawValue: trimmedName });
      setName("");
      onOpenChange(false);
    } catch {
      // onCreate surfaces the failure.
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!isSaving) onOpenChange(nextOpen);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Custom Line Item</DialogTitle>
          <DialogDescription>
            Create a manual tag for items not extracted from a schedule or takeoff. It is
            selected for tracking and exports automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="field-custom-line-item-name">Name</Label>
          <Input
            id="field-custom-line-item-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Enter custom line item"
            autoFocus
            disabled={isSaving}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void handleSubmit();
              }
            }}
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={isSaving}>
            Save Line Item
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
